import { ErrorBanner } from "@/shared/ui";
import { prettifyApiError } from "@/shared/api";
import type { CredentialMeta } from "./api";
import {
  CONNESSIONI,
  GRUPPI_CONNESSIONE,
  type Lettura,
  type StatoConnessione,
  leggiConnessione,
  nomeLane,
  quandoBreve,
} from "./automatismi";
import { formatIsoDate } from "./format";
import { Foglio, Pillola, type Tono } from "./parti";

// Le connessioni con l'esterno: canali, fornitori, corrieri, posta, conti.
// Ridisegno 28/10/2026. Prima la lista erano quattro righe fisse (Poleepo,
// IMAP, Thunderbird, PostgreSQL) e diceva «Off» a Thunderbird, che sul server
// non si usa piu'. Lo stato ora viene dall'ultimo giro degli automatismi che
// usano ogni servizio: e' gia' una prova vera, e non chiama nessuno.

const STATO_CONN: Record<StatoConnessione, { tono: Tono; nome: string }> = {
  ok: { tono: "ok", nome: "Funziona" },
  errore: { tono: "errore", nome: "Errore" },
  spenta: { tono: "spento", nome: "Non in uso" },
  mai: { tono: "spento", nome: "Mai provata" },
};

export function ConnectionsList({
  letture,
  loading,
  error = null,
}: {
  letture: Map<string, Lettura>;
  loading: boolean;
  /** Errore del caricamento degli automatismi: senza, ogni connessione
   *  risulterebbe «Non in uso», che e' un'informazione falsa. */
  error?: string | null;
}) {
  if (error) return <ErrorBanner message={error} />;
  if (loading && letture.size === 0) {
    return (
      <Foglio className="px-5 py-6">
        <p className="text-[13px] text-ink-2">Carico lo stato delle connessioni…</p>
      </Foglio>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      {GRUPPI_CONNESSIONE.map((g) => {
        const voci = CONNESSIONI.filter((c) => c.gruppo === g.id);
        return (
          <div key={g.id} className="min-w-0">
            <h3 className="mb-2 px-1 text-[13px] font-bold text-ink max-md:text-[15px]">
              {g.nome}
            </h3>
            <Foglio>
              <ul>
                {voci.map((c) => {
                  const l = leggiConnessione(c, letture);
                  const s = STATO_CONN[l.stato];
                  const motivo = l.motivo ? prettifyApiError(l.motivo) : null;
                  return (
                    <li
                      key={c.id}
                      className="flex items-start gap-3 border-t border-line px-5 py-3 first:border-t-0 max-md:px-4"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-bold text-ink max-md:text-[16px]">
                          {c.nome}
                        </div>
                        <div className="text-[12.5px] leading-snug text-ink-2 max-md:text-[14px] max-md:text-ink">
                          {c.uso}
                        </div>
                        {l.stato === "errore" && l.lane && (
                          <a
                            href={`#lane-${l.lane}`}
                            className="mt-1 block break-words text-[12.5px] leading-snug text-ink underline decoration-red underline-offset-2 max-md:text-[14px]"
                          >
                            {nomeLane(l.lane)}: {motivo ?? "ultimo giro non riuscito"}
                          </a>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <Pillola tono={s.tono}>{s.nome}</Pillola>
                        {l.quando && (
                          <span className="text-[11.5px] tabular-nums text-ink-2 max-md:text-[13px] max-md:text-ink">
                            {quandoBreve(l.quando)}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Foglio>
          </div>
        );
      })}
    </div>
  );
}

/** Le chiavi salvate in OM: solo nome e data, mai il valore (il valore sta
 *  nel keyring o nei secret del server, non nel database). */
export function ChiaviSalvate({
  credentials,
  loading,
  error = null,
}: {
  credentials: CredentialMeta[];
  loading: boolean;
  /** Un 403 NON e' una lista vuota: senza questo ramo «nessuna chiave»
   *  invitava a reinserire credenziali che funzionano. */
  error?: string | null;
}) {
  if (error) return <ErrorBanner message={error} />;
  if (loading && credentials.length === 0) {
    return <p className="px-5 py-4 text-[13px] text-ink-2">Carico le chiavi…</p>;
  }
  if (credentials.length === 0) {
    return (
      <p className="px-5 py-4 text-[13px] text-ink max-md:text-[15px]">
        Nessuna chiave salvata da qui: sul server le chiavi stanno nei file segreti.
      </p>
    );
  }
  return (
    <ul className="grid grid-cols-1 md:grid-cols-2">
      {credentials.map((c) => (
        <li
          key={c.key}
          className="flex items-baseline justify-between gap-3 border-t border-line px-5 py-2.5 max-md:px-4 md:[&:nth-child(-n+2)]:border-t-0 max-md:first:border-t-0"
        >
          <span className="min-w-0 truncate text-[13px] text-ink max-md:text-[15px]">
            {c.label}
          </span>
          <span className="shrink-0 text-[12px] tabular-nums text-ink-2 max-md:text-[13px] max-md:text-ink">
            {formatIsoDate(c.created_at).split(",")[0] || "—"}
          </span>
        </li>
      ))}
    </ul>
  );
}
